import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { HistoryFacade } from '../history-entries';
import { WorkspaceFacade } from '../workspace';
import { ArchitectureDiagram, ArchitectureLayout, ChatMessage, InkDocument } from './architecture.models';
import { ArchitectureActions } from './architecture.actions';
import {
  selectArchitectureChatMessages,
  selectArchitectureDiagram,
  selectArchitectureError,
  selectArchitectureInk,
  selectArchitectureLayout,
  selectArchitectureLoading,
  selectArchitectureProjectDefinition,
  selectArchitectureRepoContext,
} from './architecture.selectors';

@Injectable({ providedIn: 'root' })
export class ArchitectureFacade {
  private store = inject(Store);
  private workspace = inject(WorkspaceFacade);
  private history = inject(HistoryFacade);

  readonly diagram = toSignal(this.store.select(selectArchitectureDiagram), { initialValue: null });
  readonly layout = toSignal(this.store.select(selectArchitectureLayout), { initialValue: null });
  readonly ink = toSignal(this.store.select(selectArchitectureInk), { initialValue: null });
  readonly chatMessages = toSignal(this.store.select(selectArchitectureChatMessages), { initialValue: [] as ChatMessage[] });
  readonly projectDefinition = toSignal(this.store.select(selectArchitectureProjectDefinition), { initialValue: null });
  readonly repoContext = toSignal(this.store.select(selectArchitectureRepoContext), { initialValue: null });
  readonly loading = toSignal(this.store.select(selectArchitectureLoading), { initialValue: false });
  readonly error = toSignal(this.store.select(selectArchitectureError), { initialValue: null });
  readonly activeProjectId = this.workspace.activeProjectId;

  load(): void {
    this.store.dispatch(ArchitectureActions.load());
  }
  saveDiagram(diagram: ArchitectureDiagram | null): void {
    this.store.dispatch(ArchitectureActions.saveDiagram({ diagram }));
  }
  saveLayout(layout: ArchitectureLayout | null): void {
    this.store.dispatch(ArchitectureActions.saveLayout({ layout }));
  }
  saveInk(ink: InkDocument | null): void {
    this.store.dispatch(ArchitectureActions.saveInk({ ink }));
  }
  setInkDraft(ink: InkDocument | null): void {
    this.store.dispatch(ArchitectureActions.setInkDraft({ ink }));
  }
  setChatMessages(messages: ChatMessage[]): void {
    this.store.dispatch(ArchitectureActions.setChatMessages({ messages }));
  }
  async logChatEvent(text: string, kind: 'user' | 'assistant' | 'info' | 'update' | 'error' = 'info'): Promise<void> {
    const projectId = this.activeProjectId();
    if (!projectId) return;
    await this.history.add(projectId, 'architecture.chat', text, kind as any);
  }
}
